import { supabase } from "./supabaseClient";
import { coverPathsFor, occurrencesFor, pickCurrentNext } from "./events";

const EVENTS = "Events";
const EVENT_IMAGES = "Event_Images";
const EVENT_OCCURRENCES = "Event_Occurrences";
const BUCKET = "event-images";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function slugify(name) {
  const base = (name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
  return base || "event";
}

// Public geocoder for FILTERING (Calendar / Search location search). Resolves a
// free-text place, city, town, county/region or postcode/ZIP to a centre point,
// reusing the exact same backend /api/geocode infra used by event creation.
// `countryCode` biases the lookup ("GB", "US", …); pass "ANY" for an unbiased
// international lookup. Returns { lat, lng } or null. Never used to write data.
export async function geocodePlace(query, countryCode = "ANY") {
  const s = (query || "").trim();
  if (!s) return null;
  const looksPostcode = /\d/.test(s);
  let coords = { latitude: null, longitude: null };
  if (looksPostcode) coords = await geocode({ postcode: s, country_code: countryCode });
  if (coords.latitude == null) coords = await geocode({ q: s, country_code: countryCode });
  if (!Number.isFinite(coords.latitude) || !Number.isFinite(coords.longitude)) return null;
  return { lat: coords.latitude, lng: coords.longitude };
}

// Administrative boundary (county / state / city) of a searched place, for testing
// each event's map pin against the real area (support #279162). Returns
// { bbox, polygons, name } or null when the place has no usable boundary.
export async function fetchBoundary(place, countryCode = "ANY") {
  const s = (place || "").trim();
  if (s.length < 3) return null;
  try {
    const res = await fetch(`${API}/geoboundary?q=${encodeURIComponent(s)}&country_code=${encodeURIComponent(countryCode)}`);
    const d = await res.json();
    return d && d.found ? d : null;
  } catch {
    return null;
  }
}

// Best-effort geocoding via the existing backend. Postcode is primary; global-ready.
async function geocode({ postcode = "", q = "", country_code = "GB" }) {
  const params = new URLSearchParams();
  if (postcode) params.set("postcode", postcode);
  if (q) params.set("q", q);
  if (country_code) params.set("country_code", country_code);
  try {
    const res = await fetch(`${API}/geocode?${params.toString()}`);
    const d = await res.json();
    return { latitude: d.latitude ?? null, longitude: d.longitude ?? null };
  } catch {
    return { latitude: null, longitude: null };
  }
}

// Derive the IANA timezone from coordinates via the existing backend. Non-fatal.
async function timezoneFor(latitude, longitude) {
  if (latitude == null || longitude == null) return null;
  try {
    const res = await fetch(`${API}/timezone?lat=${latitude}&lng=${longitude}`);
    const d = await res.json();
    return d.timezone || null;
  } catch {
    return null;
  }
}

// Event photos (support #279162): resize so the longest side is at most 1200px
// (never enlarge a smaller photo), bake in EXIF orientation, and encode as WebP at
// 70% quality. Safari/iOS can't encode WebP (toBlob silently returns PNG), so there
// it falls back to JPEG at the same 70% quality. Only the compressed copy is ever
// uploaded — there is NO fallback to the original file: if the photo can't be
// decoded or encoded this throws, and the photo is rejected with a clear message,
// the same way profile photos work. Profile photos and banner ads have their own optimisers.
export const EVENT_PHOTO_ERROR =
  "We couldn't process this photo. Please try a different JPEG, PNG or WebP image.";
const EVENT_PHOTO_MAX = 1200;
const EVENT_PHOTO_QUALITY = 0.7;
const compressedPhotos = new WeakSet();

export async function compressEventPhoto(file) {
  if (!file || !file.type || !file.type.startsWith("image/")) throw new Error(EVENT_PHOTO_ERROR);
  let bitmap;
  try {
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      bitmap = await createImageBitmap(file);
    }
  } catch {
    throw new Error(EVENT_PHOTO_ERROR);
  }
  const scale = Math.min(1, EVENT_PHOTO_MAX / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
  if (bitmap.close) bitmap.close();
  const encode = (type) => new Promise((resolve) => canvas.toBlob(resolve, type, EVENT_PHOTO_QUALITY));
  // Browsers that can't encode WebP silently return PNG — retry as JPEG.
  let blob = await encode("image/webp");
  if (!blob || blob.type !== "image/webp") blob = await encode("image/jpeg");
  if (!blob || (blob.type !== "image/webp" && blob.type !== "image/jpeg")) throw new Error(EVENT_PHOTO_ERROR);
  const ext = blob.type === "image/webp" ? ".webp" : ".jpg";
  const out = new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ext, { type: blob.type });
  compressedPhotos.add(out);
  return out;
}

// The Add/Edit Event form compresses when the photo is chosen; anything else is
// compressed here. Throws (never returns the original) on failure.
async function ensureCompressed(file) {
  return compressedPhotos.has(file) ? file : compressEventPhoto(file);
}

// Occurrence helpers: normalise the submitted occurrence list (defaulting to the
// single primary date) and compute the overall SPAN stored on the Events row.
function normOccs(data) {
  const list =
    Array.isArray(data.occurrences) && data.occurrences.length
      ? data.occurrences
      : [{ start_at: data.start_at || null, end_at: data.end_at || null }];
  return list.filter((o) => o.start_at && o.end_at);
}
function spanOf(occs, data) {
  const starts = occs.map((o) => o.start_at).filter(Boolean).sort();
  const ends = occs.map((o) => o.end_at).filter(Boolean).sort();
  return {
    start_at: starts[0] || data.start_at || null,
    end_at: ends[ends.length - 1] || data.end_at || null,
  };
}

export async function createEvent(userId, data, imageFile, requireGeocode = true) {
  const cc = (data.country_code || "GB").toUpperCase();
  // Postcode is the PRIMARY geocoding source; fall back to the full address only
  // if postcode geocoding fails. Coords are never shown to organisers.
  let coords = { latitude: null, longitude: null };
  if (data.postcode) {
    coords = await geocode({ postcode: data.postcode, country_code: cc });
  }
  if (coords.latitude == null) {
    const fullAddress = [data.venue_name, data.address, data.postcode]
      .filter(Boolean)
      .join(", ");
    if (fullAddress) coords = await geocode({ q: fullAddress, country_code: cc });
  }
  const { latitude, longitude } = coords;
  // An event that cannot be geocoded into valid finite coordinates can never appear
  // on the map, so a normal organiser must not be able to publish it. Block BEFORE
  // inserting the row or uploading the cover image (admins are allowed through).
  if (requireGeocode && (!Number.isFinite(latitude) || !Number.isFinite(longitude))) {
    throw new Error(
      "We couldn't find this location. Check the address and postcode / ZIP code and try again."
    );
  }
  const timezone = await timezoneFor(latitude, longitude);

  // All occurrences → store the overall span on the Events row.
  const occs = normOccs(data);
  const span = spanOf(occs, data);

  const payload = {
    owner_id: userId,
    name: data.name.trim(),
    slug: slugify(data.name),
    description: data.description?.trim() || null,
    category: data.category,
    start_at: span.start_at,
    end_at: span.end_at,
    venue_name: data.venue_name?.trim() || null,
    address: data.address?.trim() || null,
    postcode: data.postcode?.trim() || null,
    country_code: cc,
    latitude,
    longitude,
    timezone,
    is_free: data.is_free,
    ticket_url: data.ticket_url?.trim() || null,
    website_url: data.website_url?.trim() || null,
    instagram_url: data.instagram_url?.trim() || null,
    facebook_url: data.facebook_url?.trim() || null,
    status: "active",
  };

  // Insert event, retrying with a short suffix if the slug collides.
  let inserted = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const { data: row, error } = await supabase
      .from(EVENTS)
      .insert(payload)
      .select()
      .single();
    if (!error) {
      inserted = row;
      break;
    }
    if (error.code === "23505") {
      payload.slug = `${slugify(data.name)}-${Math.random().toString(36).slice(2, 6)}`;
      continue;
    }
    throw error;
  }
  if (!inserted) throw new Error("Could not create a unique event slug. Please try again.");

  // Cover image → event-images bucket + Event_Images position 1 only.
  // NOTE: The exact Event_Images column names could not be verified without an
  // authenticated session (anon has no read access). This block is therefore
  // best-effort and NON-FATAL: if the storage path RLS or column names differ,
  // the event still saves and we return a warning for the caller to surface.
  let imageWarning = null;
  if (imageFile) {
    try {
      const compressed = await ensureCompressed(imageFile);
      const ext = (compressed.name.split(".").pop() || "jpg")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      const path = `${userId}/${inserted.id}/${crypto.randomUUID()}.${ext || "jpg"}`;

      const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, compressed, { contentType: compressed.type, upsert: false });
      if (upErr) throw upErr;

      const { error: imgErr } = await supabase.from(EVENT_IMAGES).insert({
        event_id: inserted.id,
        storage_path: path,
        position: 1,
      });
      if (imgErr) throw imgErr;
    } catch (e) {
      imageWarning =
        "Event saved, but the cover image could not be attached yet. " +
        (e?.message || "Please confirm the Event_Images columns / storage policy.");
      // Surface exact error to console for debugging without breaking the flow.
      // eslint-disable-next-line no-console
      console.warn("[RoadRnr] Event_Images/cover upload failed:", e);
    }
  }

  // Additional occurrences (2+ total) → child rows. Single-date events keep none.
  if (occs.length >= 2) {
    const { error: occErr } = await supabase
      .from(EVENT_OCCURRENCES)
      .insert(occs.map((o) => ({ event_id: inserted.id, start_at: o.start_at, end_at: o.end_at })));
    if (occErr) {
      imageWarning = [imageWarning, "Event saved, but its extra dates couldn't be attached. " + (occErr.message || "")]
        .filter(Boolean)
        .join(" ");
      // eslint-disable-next-line no-console
      console.warn("[RoadRnr] Event_Occurrences insert failed:", occErr);
    }
  }

  // A user event wins over any matching admin auto-managed copy: ask the backend to
  // hide the admin duplicate (never touches this user event). Best-effort / non-fatal.
  try {
    await fetch(`${API}/events/${inserted.id}/reconcile-admin-duplicates`, { method: "POST" });
  } catch {
    /* non-fatal — reconciliation also runs in daily maintenance */
  }

  return { event: inserted, imageWarning };
}

// Upload a new cover image and attach it as Event_Images position 1 (update if one
// already exists, else insert). Non-fatal: returns a warning string or null.
async function upsertCover(userId, eventId, imageFile) {
  try {
    const compressed = await ensureCompressed(imageFile);
    const ext = (compressed.name.split(".").pop() || "jpg")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    const path = `${userId}/${eventId}/${crypto.randomUUID()}.${ext || "jpg"}`;

    const { error: upErr } = await supabase.storage
      .from(BUCKET)
      .upload(path, compressed, { contentType: compressed.type, upsert: false });
    if (upErr) throw upErr;

    const { data: existing } = await supabase
      .from(EVENT_IMAGES)
      .select("id,storage_path")
      .eq("event_id", eventId)
      .eq("position", 1)
      .limit(1);

    if (existing && existing[0]) {
      const oldPath = existing[0].storage_path;
      const { error } = await supabase
        .from(EVENT_IMAGES)
        .update({ storage_path: path })
        .eq("id", existing[0].id);
      if (error) throw error;
      // Remove the OLD cover file from Storage now that the record points at the
      // new one. Non-fatal — a leftover file must never block the save, but any
      // block is surfaced to the caller as a warning.
      if (oldPath && oldPath !== path) {
        const { error: rmErr } = await supabase.storage.from(BUCKET).remove([oldPath]);
        if (rmErr) {
          // eslint-disable-next-line no-console
          console.warn("[RoadRnr] old cover cleanup failed:", rmErr);
          return (
            "Cover updated, but the previous image file could not be removed from storage: " +
            (rmErr.message || String(rmErr))
          );
        }
      }
    } else {
      const { error } = await supabase
        .from(EVENT_IMAGES)
        .insert({ event_id: eventId, storage_path: path, position: 1 });
      if (error) throw error;
    }
    return null;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[RoadRnr] cover upsert failed:", e);
    return "Saved, but the cover image could not be updated. " + (e?.message || "");
  }
}

// My Events: raw owner rows (all statuses) + cover storage PATHS only (one batched
// query, NO signing). Each row's thumbnail signs its own display-sized image lazily
// via useCoverUrl, so the list renders immediately instead of waiting on N signed-URL
// network calls. Relies on existing owner RLS (owner_id = auth.uid()).
export async function fetchOwnEvents(userId) {
  const { data, error } = await supabase
    .from(EVENTS)
    .select("*")
    .eq("owner_id", userId)
    .order("start_at", { ascending: false });
  if (error) throw error;
  const ids = (data || []).map((r) => r.id);
  const [paths, occMap] = await Promise.all([coverPathsFor(ids), occurrencesFor(ids)]);
  const now = Date.now();
  // Display the current/next occurrence date (span stays available as span_*).
  return (data || []).map((row) => {
    const occs = occMap[row.id]?.length ? occMap[row.id] : [{ start_at: row.start_at, end_at: row.end_at }];
    const cn = pickCurrentNext(occs, now);
    return {
      ...row,
      coverPath: paths[row.id] || null,
      span_start: row.start_at,
      span_end: row.end_at,
      start_at: cn.start_at || row.start_at,
      end_at: cn.end_at || row.end_at,
    };
  });
}

// Raw owner row for pre-filling the edit form (any status), with its occurrence
// rows attached (empty for single-date events).
export async function fetchOwnEvent(id) {
  const { data, error } = await supabase.from(EVENTS).select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  if (!data) return data;
  const occRows = await occurrencesFor([id]);
  data.occurrences = occRows[id] || [];
  return data;
}

// Update an existing owned event. Keeps id, slug, owner_id and status unchanged.
// Re-geocodes only when a location field changed (or coords were missing).
export async function updateEvent(id, data, imageFile, existing, requireGeocode = true) {
  const cc = (data.country_code || "GB").toUpperCase();
  const occs = normOccs(data);
  const span = spanOf(occs, data);
  const payload = {
    name: data.name.trim(),
    description: data.description?.trim() || null,
    category: data.category,
    start_at: span.start_at,
    end_at: span.end_at,
    venue_name: data.venue_name?.trim() || null,
    address: data.address?.trim() || null,
    postcode: data.postcode?.trim() || null,
    country_code: cc,
    is_free: data.is_free,
    ticket_url: data.ticket_url?.trim() || null,
    website_url: data.website_url?.trim() || null,
    instagram_url: data.instagram_url?.trim() || null,
    facebook_url: data.facebook_url?.trim() || null,
  };

  const locChanged =
    !existing ||
    (data.postcode || "") !== (existing.postcode || "") ||
    cc !== (existing.country_code || "GB") ||
    (data.address || "") !== (existing.address || "") ||
    (data.venue_name || "") !== (existing.venue_name || "");

  if (locChanged || existing?.latitude == null) {
    let coords = { latitude: null, longitude: null };
    if (data.postcode) coords = await geocode({ postcode: data.postcode, country_code: cc });
    if (coords.latitude == null) {
      const full = [data.venue_name, data.address, data.postcode].filter(Boolean).join(", ");
      if (full) coords = await geocode({ q: full, country_code: cc });
    }
    // A qualifying location change must still resolve to valid coordinates, or the
    // event would become unmappable. Block before the DB update (admins exempt).
    if (
      requireGeocode &&
      (!Number.isFinite(coords.latitude) || !Number.isFinite(coords.longitude))
    ) {
      throw new Error(
        "We couldn't find this location. Check the address and postcode / ZIP code and try again."
      );
    }
    payload.latitude = coords.latitude;
    payload.longitude = coords.longitude;
    payload.timezone = await timezoneFor(coords.latitude, coords.longitude);
  }

  const { data: row, error } = await supabase
    .from(EVENTS)
    .update(payload)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;

  // Replace occurrence rows to match the submitted set: clear then re-insert when
  // 2+ occurrences; single-date events keep none.
  await supabase.from(EVENT_OCCURRENCES).delete().eq("event_id", id);
  if (occs.length >= 2) {
    await supabase
      .from(EVENT_OCCURRENCES)
      .insert(occs.map((o) => ({ event_id: id, start_at: o.start_at, end_at: o.end_at })));
  }

  let imageWarning = null;
  if (imageFile) imageWarning = await upsertCover(row.owner_id, id, imageFile);
  return { event: row, imageWarning };
}

// Cancel (soft): set status = 'cancelled'. Never deletes the row.
export async function cancelEvent(id) {
  const { data, error } = await supabase
    .from(EVENTS)
    .update({ status: "cancelled" })
    .eq("id", id)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(
      "Could not cancel — the update affected no rows (check owner UPDATE RLS policy)."
    );
  }
  return true;
}

// Permanently delete an owned event. Removes the EXACT cover file(s) from Storage
// and CONFIRMS the removal succeeded BEFORE deleting the Events row. If the
// storage delete is not confirmed, the exact error is surfaced and the row is
// left intact (no orphaned file, no half-delete). Event_Images rows are removed
// by the existing FK cascade once the row is deleted.
export async function deleteEvent(id) {
  // 1. Exact cover path(s) straight from Event_Images — never reconstructed.
  const { data: imgs, error: imgErr } = await supabase
    .from(EVENT_IMAGES)
    .select("storage_path")
    .eq("event_id", id);
  if (imgErr) throw imgErr;
  const paths = (imgs || []).map((i) => i.storage_path).filter(Boolean);

  // 2. Remove the exact file(s) and CONFIRM success before touching the row.
  //    Supabase returns [] (HTTP 200, no error) when a delete is silently
  //    blocked by Storage RLS or the path doesn't match — so success is only
  //    real when the returned data contains each requested path.
  if (paths.length) {
    const { data: removed, error: rmErr } = await supabase.storage.from(BUCKET).remove(paths);
    if (rmErr) {
      throw new Error(
        "Storage delete failed for the cover image — " +
          (rmErr.message || String(rmErr)) +
          ". The event was not deleted."
      );
    }
    const removedNames = new Set((removed || []).map((f) => f.name));
    const missing = paths.filter((p) => !removedNames.has(p));
    if (missing.length) {
      throw new Error(
        "Storage delete not confirmed — the cover file was not removed from " +
          `event-images (${missing.join(", ")}). The event was not deleted. ` +
          "This usually means the bucket has no owner DELETE policy."
      );
    }
  }

  // 3. Storage removal confirmed — now permanently delete the event row.
  const { data, error } = await supabase.from(EVENTS).delete().eq("id", id).select("id");
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(
      "Could not delete — the delete affected no rows (an owner DELETE RLS policy is required)."
    );
  }
  return { storageWarning: null };
}
